import React from 'react';
import { Meeting } from '../../types';

interface MeetingPerformanceSectionImprovedProps {
  meeting: Meeting;
}

export const MeetingPerformanceSectionImproved: React.FC<MeetingPerformanceSectionImprovedProps> = ({ meeting }) => {
  // Format ROI display - no fallback calculation as it would be inaccurate
  // Real ROI calculation requires actual starting prices from race results
  const formatROI = (roi: number | null | undefined, pickType: 'top_1' | 'value' = 'top_1') => {
    if (roi === null || roi === undefined) {
      return <span className="text-gray-500">N/A</span>;
    }
    
    const color = roi >= 0 ? 'text-racing-green' : 'text-racing-red';
    return <span className={color}>{roi >= 0 ? '+' : ''}{roi.toFixed(1)}%</span>;
  };

  if (!meeting.performanceImproved) {
    return (
      <div className="bg-white rounded-lg shadow-card mb-6 p-6">
        <h2 className="text-xl font-bold text-white bg-deep-navy p-3 -m-6 mb-4 rounded-t-lg">
          Meeting Performance Summary
        </h2>
        <p className="text-gray-500">No performance data available for this meeting.</p>
      </div>
    );
  }

  const perf = meeting.performanceImproved;

  return (
    <div className="bg-white rounded-lg shadow-card mb-6">
      <h2 className="text-xl font-bold text-white bg-deep-navy p-3 rounded-t-lg">
        Meeting Performance Summary
      </h2>
      
      <div className="p-6">
        {/* ROI Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-racing-gray p-3 rounded">
            <p className="text-sm text-gray-600 mb-1">Top Pick ROI</p>
            <p className="font-bold text-xl">{formatROI(meeting.performanceImproved.top_1_roi, 'top_1')}</p>
          </div>
          <div className="bg-racing-gray p-3 rounded">
            <p className="text-sm text-gray-600 mb-1">Value Pick ROI</p>
            <p className="font-bold text-xl">{formatROI(meeting.performanceImproved.value_roi, 'value')}</p>
          </div>
        </div>

        {/* Tips Performance Summary */}
        <h3 className="text-lg font-bold mb-4">Tips Performance Summary</h3>
        
        {/* Overall Performance */}
        <div className="mb-6">
          <h4 className="font-bold mb-2">Overall Performance</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Winners:</p>
              <p className="font-bold text-lg">
                {(perf.top_1_winners + perf.top_2_winners + perf.top_3_winners + perf.value_winners)}/{perf.total_races}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Places:</p>
              <p className="font-bold text-lg">
                {(perf.top_1_places + perf.top_2_places + perf.top_3_places + perf.value_places)}/
                {(perf.top_1_picks - perf.top_1_scratched + perf.top_2_picks - perf.top_2_scratched + 
                  perf.top_3_picks - perf.top_3_scratched + perf.value_picks - perf.value_scratched)} (
                {(((perf.top_1_places + perf.top_2_places + perf.top_3_places + perf.value_places) / 
                   (perf.top_1_picks - perf.top_1_scratched + perf.top_2_picks - perf.top_2_scratched + 
                    perf.top_3_picks - perf.top_3_scratched + perf.value_picks - perf.value_scratched)) * 100).toFixed(1)}%)
              </p>
            </div>
          </div>
        </div>

        {/* Strike Rates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-bold mb-2">1st Pick Strike Rate</h4>
            <p className="text-lg">
              {perf.top_1_winners}/{perf.top_1_picks - perf.top_1_scratched} (
              {perf.top_1_picks - perf.top_1_scratched > 0 ? 
                ((perf.top_1_winners / (perf.top_1_picks - perf.top_1_scratched)) * 100).toFixed(2) : '0.00'}%)
            </p>
            <p className="text-sm text-gray-600">
              Win: {perf.top_1_winners}, Place: {perf.top_1_places}
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-2">2nd Pick Strike Rate</h4>
            <p className="text-lg">
              {perf.top_2_winners}/{perf.top_2_picks - perf.top_2_scratched} (
              {perf.top_2_picks - perf.top_2_scratched > 0 ? 
                ((perf.top_2_winners / (perf.top_2_picks - perf.top_2_scratched)) * 100).toFixed(2) : '0.00'}%)
            </p>
            <p className="text-sm text-gray-600">
              Win: {perf.top_2_winners}, Place: {perf.top_2_places}
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-2">3rd Pick Strike Rate</h4>
            <p className="text-lg">
              {perf.top_3_winners}/{perf.top_3_picks - perf.top_3_scratched} (
              {perf.top_3_picks - perf.top_3_scratched > 0 ? 
                ((perf.top_3_winners / (perf.top_3_picks - perf.top_3_scratched)) * 100).toFixed(2) : '0.00'}%)
            </p>
            <p className="text-sm text-gray-600">
              Win: {perf.top_3_winners}, Place: {perf.top_3_places}
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-2">Value Pick Strike Rate</h4>
            <p className="text-lg">
              {perf.value_winners}/{perf.value_picks - perf.value_scratched} (
              {perf.value_picks - perf.value_scratched > 0 ? 
                ((perf.value_winners / (perf.value_picks - perf.value_scratched)) * 100).toFixed(2) : '0.00'}%)
            </p>
            <p className="text-sm text-gray-600">
              Win: {perf.value_winners}, Place: {perf.value_places}
            </p>
          </div>
        </div>

        {/* Exotic Bets Performance */}
        {meeting.exoticBets && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-bold mb-4">Exotic Bets Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold mb-2">Exacta Success Rate</h4>
                  <p className="text-lg">
                    {meeting.exoticBets.exactas || 0}/{perf.total_races} (
                    {perf.total_races > 0 ? 
                      ((meeting.exoticBets.exacta_percentage || 0)).toFixed(1) : '0.0'}%)
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2">Trifecta Success Rate</h4>
                  <p className="text-lg">
                    {meeting.exoticBets.trifectas || 0}/{perf.total_races} (
                    {perf.total_races > 0 ? 
                      ((meeting.exoticBets.trifecta_percentage || 0)).toFixed(1) : '0.0'}%)
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2">First Four Success Rate</h4>
                  <p className="text-lg">
                    {meeting.exoticBets.first_fours || 0}/{perf.total_races} (
                    {perf.total_races > 0 ? 
                      ((meeting.exoticBets.first_four_percentage || 0)).toFixed(1) : '0.0'}%)
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold mb-2">Quinella Success Rate</h4>
                  <p className="text-lg">
                    {meeting.exoticBets.quinellas || 0}/{perf.total_races} (
                    {perf.total_races > 0 ? 
                      ((meeting.exoticBets.quinella_percentage || 0)).toFixed(1) : '0.0'}%)
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2">Box Trifecta Success Rate</h4>
                  <p className="text-lg">
                    {meeting.exoticBets.box_trifectas || 0}/{perf.total_races} (
                    {perf.total_races > 0 ? 
                      ((meeting.exoticBets.box_trifecta_percentage || 0)).toFixed(1) : '0.0'}%)
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2">Box First Four Success Rate</h4>
                  <p className="text-lg">
                    {meeting.exoticBets.box_first_fours || 0}/{perf.total_races} (
                    {perf.total_races > 0 ? 
                      ((meeting.exoticBets.box_first_four_percentage || 0)).toFixed(1) : '0.0'}%)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingPerformanceSectionImproved;
